import {
  Arrow,
  Heading,
  Modal,
  Select,
  StatusBadge,
  Warning,
  theme,
} from '@lmc/ui-kit'
import { ActionModalProps } from '..'
import styled from 'styled-components'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Text } from '@lmc/ui-kit/src/general'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { toDosConstants } from 'locale/toDos'
import CommentSection, { ITEM_NAME } from '../components/CommentSection'
import { Task, TaskStatus } from '@lmc/ts-common'
import { postJSON } from 'common/services/api'
import { useMutation } from 'react-query'
import { showToast } from 'common/utils'
import { Form } from 'antd'
import { generalConstants } from 'locale/general'
import { useDispatch } from 'react-redux'
import actions from 'actions'

const taskStatusRoutes = {
  [TaskStatus.completed]: 'complete',
  [TaskStatus.skipped]: 'skip',
  [TaskStatus.pending]: 'incomplete',
}

type FormResult = {
  comment: string
  status: keyof typeof taskStatusRoutes
}

const StatusModal = ({
  task,
  isModalVisible,
  setIsModalVisible,
  onSuccess,
}: ActionModalProps) => {
  const { t } = useTranslation(NAMESPACE.TODOS)
  const [form] = Form.useForm<FormResult>()
  const dispatch = useDispatch()

  const selectedStatus = Form.useWatch('status', form)
  const willRequireLog = selectedStatus === TaskStatus.completed && task.logScreenPrompt

  const mutationFunction = (body: {
    comment: string
    statusPath: string
  }): Promise<{
    result: Task
  }> =>
    postJSON({
      method: 'POST',
      url: `list/tasks/${task.id}/${body.statusPath}`,
      json: { note: body.comment },
    })

  const { mutateAsync, isLoading } = useMutation(mutationFunction, {
    onSuccess: () => {
      showToast({ title: t(toDosConstants.STATUS_CHANGED) })
      setIsModalVisible(false)
      onSuccess?.()
    },
  })

  const openLogModalForTask = () => {
    dispatch(
      actions.addForm({
        formId: 'addLog',
        fields: {
          resident: task.residentData?.id,
          category: task.category,
          item: task.item,
          title: task.title,
          description: '', 
          _taskId: task.id,
          _taskStatusPath: 'complete',
          _taskComment: '',
          _taskOnSuccess: onSuccess,
        } as any, // Type definition is incorrect, accepts object in practice
      })
    )

    // Open the log modal with taskCompletion flag
    dispatch(
      actions.openModal({
        modalId: 'addLog',
        data: {
          taskCompletion: true, // Flag to indicate this is from task completion
        },
      })
    )
  }

  const onSubmit = (values: FormResult) => {
    // Check if we need to open log modal for task completion
    if (willRequireLog) {
      openLogModalForTask()
      setIsModalVisible(false) // Close status modal
      return
    }

    // Original flow for non-log tasks
    const newValues = {
      ...values,
      statusPath: taskStatusRoutes[values.status],
    }

    mutateAsync(newValues)
  }

  const statusOptions =
    Object.keys(taskStatusRoutes)
      .filter((status) => status !== task.status)
      .map((statusType) => ({
        label: <StyledStatusType>{statusType}</StyledStatusType>,
        value: statusType,
      })) || []

  return (
    <Modal
      closeButton
      isModalVisible={isModalVisible}
      setIsModalVisible={setIsModalVisible}
      classicPadding
      closeOnConfirm={false}
      width580px
      onConfirm={form.submit}
      onCancel={() => ''}
      localisation={{
        confirmText: t(
          willRequireLog 
            ? 'general.buttons.next' 
            : 'general.buttons.save'
          , 
          {
            ns: NAMESPACE.RESOURCES,
          }
        ),
        cancelText: t('general.buttons.cancel', {
          ns: NAMESPACE.RESOURCES,
        }),
      }}
      confirmButtonProps={{ variant: 'primary', isLoading }}
    >
      <TitleContainer>
        <Warning size={24} color={theme.color.status.warning} />
        <Title level={3}>{t(toDosConstants.CHANGE_STATUS)}</Title>
      </TitleContainer>
      <Divider />
      <Form onFinish={onSubmit} form={form}>
        <EditStatusContainer>
          <div>
            <StatusContainer>
              <StatusModalText weight="bold">
                {t(toDosConstants.CURRENT_STATUS)}
              </StatusModalText>
              <Status>
                <StatusBadgeStyled status={task.calculatedStatus} />
                <StatusModalText>{task.calculatedStatus}</StatusModalText>
              </Status>
            </StatusContainer>
          </div>
          <ArrowContainer>
            <Arrow direction="right" size={24} />
          </ArrowContainer>
          <StatusContainer>
            <StatusModalText weight="bold">
              {t(toDosConstants.NEW_STATUS)}
            </StatusModalText>
            <SelectContainer>
              <Form.Item
                name="status"
                required
                rules={[
                  {
                    required: true,
                    message: t(generalConstants.REQUIRED_MESSAGE, {
                      ns: NAMESPACE.GENERAL,
                    }),
                  },
                ]}
              >
                <StyledSelect
                  optionLabelProp="label"
                  size="large"
                  placeholder={
                    <Text>{t(toDosConstants.SELECT_NEW_STATUS)}</Text>
                  }
                  options={statusOptions}
                />
              </Form.Item>
            </SelectContainer>
          </StatusContainer>
        </EditStatusContainer>
        {!willRequireLog && (
          <Form.Item name={ITEM_NAME}>
            <CommentSection required={false} />
          </Form.Item>
        )}
      </Form>
      <Divider />
    </Modal>
  )
}
const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
`
const Title = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

const EditStatusContainer = styled.div`
  margin: 24px 0 32px;
  display: flex;
  justify-content: space-between;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
  }
`

const StatusContainer = styled.div`
  width: 200px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: auto;
  }
`

const StatusModalText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color3};
`

const Status = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
  margin: 8px 0 0 -8px;
  text-transform: capitalize;
`

const StyledStatusType = styled.div`
  text-transform: capitalize;
`

const ArrowContainer = styled.div`
  display: flex;
  align-items: flex-end;
  margin-bottom: 10px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    display: none;
  }
`

const SelectContainer = styled.div`
  margin-top: 12px;
`
const StyledSelect = styled(Select)`
  min-width: 200px;
` as typeof Select

const StatusBadgeStyled = styled(StatusBadge)`
  padding: 4px 8px;
`

export default StatusModal
